import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, YAxis } from 'recharts';
import { useIntl } from 'react-intl';
import { styled } from 'stitches.config';
import { toCurrency } from '@/utils/numberFormatter';
import Typography from '@/components/sushi/Typography';
import { palette } from 'theme/palette';
import { SimulatorLegend } from '../simulators/SimulatorLegend';
import { CompoundInterestsTooltip } from './CompoundInterestsTooltip';

const PLACEHOLDER_DATA: CompoundInterestsGraphData[] = [
    {
        "year": 1,
        "interests": 105000,
        "payments": 12000,
        "netValue": 117000
    },
    {
        "year": 2,
        "interests": 110250,
        "payments": 24000,
        "netValue": 134250
    },
    {
        "year": 3,
        "interests": 115762.50000000001,
        "payments": 36000,
        "netValue": 151762.5
    },
    {
        "year": 4,
        "interests": 121550.62500000003,
        "payments": 48000,
        "netValue": 169550.62500000003
    },
    {
        "year": 5,
        "interests": 127628.15625000003,
        "payments": 60000,
        "netValue": 187628.15625000003
    },
    {
        "year": 6,
        "interests": 134009.56406250005,
        "payments": 72000,
        "netValue": 206009.56406250005
    },
    {
        "year": 7,
        "interests": 140710.04226562506,
        "payments": 84000,
        "netValue": 224710.04226562506
    },
    {
        "year": 8,
        "interests": 147745.5443789063,
        "payments": 96000,
        "netValue": 243745.5443789063
    },
    {
        "year": 9,
        "interests": 155132.8215978516,
        "payments": 108000,
        "netValue": 263132.8215978516
    },
    {
        "year": 10,
        "interests": 162889.46267774422,
        "payments": 120000,
        "netValue": 282889.4626777442
    },
    {
        "year": 11,
        "interests": 171033.9358116314,
        "payments": 132000,
        "netValue": 303033.9358116314
    },
    {
        "year": 12,
        "interests": 179585.632602213,
        "payments": 144000,
        "netValue": 323585.632602213
    },
    {
        "year": 13,
        "interests": 188564.91423232367,
        "payments": 156000,
        "netValue": 344564.91423232364
    },
    {
        "year": 14,
        "interests": 197993.15994393986,
        "payments": 168000,
        "netValue": 365993.1599439399
    },
    {
        "year": 15,
        "interests": 207892.81794113686,
        "payments": 180000,
        "netValue": 387892.8179411369
    },
    {
        "year": 16,
        "interests": 218287.4588381937,
        "payments": 192000,
        "netValue": 410287.4588381937
    },
    {
        "year": 17,
        "interests": 229201.8317801034,
        "payments": 204000,
        "netValue": 433201.8317801034
    },
    {
        "year": 18,
        "interests": 240661.9233691086,
        "payments": 216000,
        "netValue": 456661.9233691086
    },
    {
        "year": 19,
        "interests": 252695.01953756405,
        "payments": 228000,
        "netValue": 480695.019537564
    },
    {
        "year": 20,
        "interests": 265329.77051444224,
        "payments": 240000,
        "netValue": 505329.77051444224
    }
]

export type CompoundInterestsGraphData = {
    payments: number;
    interests: number;
    netValue: number;
    year: number;
}

export type CompoundInterestsGraphProps = {
  graphData: CompoundInterestsGraphData[];
  nbYears: number;
  displayPlaceholder?: boolean;
  height?: number;
  /**
   * If true, will use simple interests translations
   */
  isSimpleInterestsGraph?: boolean;
}



export const CompoundInterestsGraph = ({ graphData, nbYears, displayPlaceholder, isSimpleInterestsGraph, height = 280 }: CompoundInterestsGraphProps) => {
  const { formatMessage } = useIntl();

  return (
    <div>
      <GraphContainer height={height} width="100%">
        <AreaChart data={displayPlaceholder ? PLACEHOLDER_DATA : graphData} margin={{ top: 15 }}>
          <CartesianGrid vertical={false} strokeDasharray="2 2" stroke="#2E2F33" />

          <YAxis
            tickLine={false}
            tick={{ fontSize: 10, transform: 'translate(56, -9)' }}
            axisLine={false}
            tickMargin={0}
            tickFormatter={(props) => toCurrency(props, 'EUR', 0, { notation: 'compact' })}
          />
          {!displayPlaceholder ? (
            <Tooltip
              wrapperStyle={{ outline: 'none' }}
              content={({ payload }) => {
                if (!payload[0]) {
                  return null;
                }
                const data = payload[0].payload;

                return (
                  <CompoundInterestsTooltip
                    isSimpleInterestsGraph={isSimpleInterestsGraph}
                    data={data}
                  />
                );
              }}
            />
          ) : null}
          <Area
            type="monotone"
            dataKey="payments"
            stackId="1"
            strokeWidth={2}
            activeDot={{ r: 4, stroke: '0' }}
            stroke={displayPlaceholder ? '#333333' : palette.sky400}
            fill={displayPlaceholder ? '#333333' : palette.sky400}
          />
          <Area
            type="monotone"
            dataKey="interests"
            stackId="1"
            strokeWidth={2}
            activeDot={{ r: 4, stroke: '0' }}
            stroke={displayPlaceholder ? '#333333' : palette.brand500}
            fill={displayPlaceholder ? '#333333' : palette.brand500}
          />
        </AreaChart>
      </GraphContainer>
      <AxisValues>
        <Typography variant="bodyXS" color="webappTertiary">
          {formatMessage({ id: 'wealth_simulator.chart.axis_min' })}
        </Typography>
        <Typography variant="bodyXS" color="webappTertiary">{`${nbYears} ${formatMessage({
          id: 'wealth_simulator.chart.axis_max',
        })}`}</Typography>
      </AxisValues>
      <SimulatorLegend items={[
        {
            label: formatMessage({ id: isSimpleInterestsGraph ? 'simple_interests_simulator.chart.legend.payments' : 'compound_interests_simulator.chart.legend.payments' }),
            color: palette.sky400,
        },
        {
            label: formatMessage({ id: isSimpleInterestsGraph ? 'simple_interests_simulator.chart.legend.interests' : 'compound_interests_simulator.chart.legend.interests' }),
            color: palette.brand500,
        }
      ]} />
    </div>
  );
};

const GraphContainer = styled(ResponsiveContainer, {
  // By default rechart has a margin left that we can't remove if we want to display the labels of the axis
  // This is a trick to display it the way we want anyway (ie with no margin - which are SVG calculated)
  width: 'calc(100% + 60px) !important',
  marginLeft: '-60px',
});

const AxisValues = styled('div', {
  display: 'flex',
  justifyContent: 'space-between',
  padding: '8px 30px 16px',
});